// no itx for this file
import type ButtonChannel from "./ButtonChannel";
import type Channel from "./Channel";
import type Device from "./Device";

export enum ChannelType {
  Unknown = "Unknown",
  Switch = "Switch",
  StaircaseLight = "StaircaseLight",
  Dimmer = "Dimmer",
  TuneableWhite = "TuneableWhite",
  Rgb = "Rgb",
  // Rgbw = "Rgbw",
  Heating = "Heating",
  TemperatureSetpoint = "TemperatureSetpoint",
  TemperatureMeasurement = "TemperatureMeasurement",
  Jalousie = "Jalousie",
  Blind = "Blind",
  Lock = "Lock",
  Scene = "Scene",
  General = "General",
  suply230V = "suply230V",
  suply400V = "suply400V",
  suply24V = "suply24V",
  suply12V = "suply12V",
  Lan = "Lan",
  DoorLock = "DoorLock",
  Camera = "Camera",
  // Verbraucher der 2 Aktorkanaele (Ports) gleichzeitig belegt, z.B. eine doppelte LAN-Dose
  Lan2 = "Lan2",
  plug_230V = "plug_230V",
  plug_Switch = "plug_Switch",
  plug_Switch1 = "plug_Switch1",
  plug_Switch2 = "plug_Switch2",
  plug_Switch3 = "plug_Switch3",
  plug_LAN = "plug_LAN",
  plug_LAN2 = "plug_LAN2",
  plug_USB = "plug_USB",
  plug_TV = "plug_TV",
  StatusDisplaySwitch = "StatusDisplaySwitch",
  StatusDisplayDimValue = "StatusDisplayDimValue",
  StatusDisplayTemperature = "StatusDisplayTemperature",
  Environment = "Environment",
  Generic = "Generic",
  Disabled = "Disabled",

  OperationMode = "OperationMode",

  //
  // Zwischenverteilung für Zimmer an 7.8.2026 mit ConceptOne eingeführt
  //
  IntermediateDistribution = "IntermediateDistribution",

  // Achtung
  // nur für temporäre Berechungen // darf nich an einen Channel etc geschriben werden
  //
  PlugSwitchable = "PlugSwitchable",
  Plug = "Plug",

  // MWE 14.2.2025
  //
  // wenn eine Channel als Unkown erkannt wird,
  // aber mit einem Script auf einen TAG gemapped wird,
  // soll dieser Channel im UI nicht verloren gehen
  // Daher wird beim mappen dann "Unknown" in "UnknownMapWithScript" umgewandelt
  //
  UnknownMapWithScript = "UnknownMapWithScript",
  GenericValue = "GenericValue",
  Ignored = "Ignored",
  ManualSignals = "ManualSignals",

  Special = "Special", // nur für Buttons, wenn das Signal sonst niegendwo passt
  FromKnxChannel = "FromKnxChannel", // nur für Buttons temporär, wenn der ChannelType in den Parametern manuell eingestellt wurde
}

export const PLUG_CHANNEL_TYPES = [
  ChannelType.plug_230V, ChannelType.plug_Switch, ChannelType.plug_Switch1,
  ChannelType.plug_Switch2, ChannelType.plug_Switch3, ChannelType.plug_LAN,
  ChannelType.plug_LAN2, ChannelType.plug_USB, ChannelType.plug_TV,
] as const;
export type PlugChannelType = (typeof PLUG_CHANNEL_TYPES)[number];

export function isPlugChannelType(type: string | undefined): type is PlugChannelType {
  return PLUG_CHANNEL_TYPES.some(value => value === type);
}
export function isSwitchedPlugChannelType(type: string | undefined): boolean {
  return type?.startsWith("plug_Switch") === true;
}
export function isCablelessPlugChannelType(type: string | undefined): boolean {
  return type === ChannelType.plug_USB || type === ChannelType.plug_TV;
}
/** The persisted device type stays specific; actors and signals use the electrical function. */
export function electricalChannelType(type: ChannelType): ChannelType {
  if (isSwitchedPlugChannelType(type)) return ChannelType.Switch;
  switch (type) {
    case ChannelType.plug_230V: return ChannelType.suply230V;
    case ChannelType.plug_LAN: return ChannelType.Lan;
    case ChannelType.plug_LAN2: return ChannelType.Lan2;
    default: return type;
  }
}

export const specialChannelTypes = [
  ChannelType.Environment,
  ChannelType.General,
  ChannelType.Unknown,
  ChannelType.UnknownMapWithScript,
  ChannelType.Special,
  ChannelType.Ignored,
  ChannelType.ManualSignals,
];

export enum DeviceType {
  Unknown = "Unknown",
  Generic = "Generic",
  Switch = "Switch",
  Dimmer = "Dimmer",
  TuneableWhite = "TuneableWhite",
  Rgb = "Rgb",
  // Rgbw = "Rgbw",
  Heating = "Heating",
  Jalousie = "Jalousie",
  Blind = "Blind",
  Lock = "Lock",
  Input = "Input",
  Lan = "Lan",
  suply230V = "suply230V",
  suply400V = "suply400V",
  suply24V = "suply24V",
  suply12V = "suply12V",
}

export type ChannelTypeInfo = {
  isPlug?: boolean;
  isSupply: boolean;
  canBeConnectedWithPartner: boolean;
  canBeConnectedWithButtonChannel: boolean;
  temperatureAndHeatingChannelTypes: boolean;
  isStatusChannel: boolean;
  isLight: boolean;
  isLight230V?: boolean;
  isRollo: boolean;
  isHeating: boolean;
  image: string;
  isSettableInCustomizing: boolean;
  canBeConsumer: boolean;
  hasNoBundle: boolean; // if true, there is no SignalBundle for this ChannelType (e.g. Unknown, Ignored, ManualSignals)
  channelSlotSize?: number; // number of sequential actor-channel slots occupied when auto-connected (default 1); Lan2 occupies 2 slots and shows a combined tag like 1,2
  isLan?: boolean; // true for all channel types that represent a LAN (network patch panel) connection, e.g. Lan and Lan2
};

export function channelTypeHirarchie(def: "isLight" | "isRollo" | "isHeating") {
  if (def == "isLight") {
    return [
      ChannelType.Switch,
      ChannelType.StaircaseLight,
      ChannelType.Dimmer,
      ChannelType.TuneableWhite,
      ChannelType.Rgb,
      // ChannelType.Rgbw,
    ];
  } else if (def == "isRollo") {
    return [ChannelType.Blind, ChannelType.Jalousie];
  } else if (def == "isHeating") {
    return [ChannelType.TemperatureMeasurement, ChannelType.TemperatureSetpoint, ChannelType.Heating];
  }
}

export function channelTypeInfo(channeltype: ChannelType): ChannelTypeInfo {
  const image = getChannelTypePicture(channeltype);
  if (isPlugChannelType(channeltype)) {
    const cableless = isCablelessPlugChannelType(channeltype);
    const base = channelTypeInfo(cableless ? ChannelType.Generic : electricalChannelType(channeltype));
    return { ...base, image, isPlug: true, isLight: false,
      canBeConsumer: true, isSettableInCustomizing: false,
      canBeConnectedWithPartner: !cableless,
      canBeConnectedWithButtonChannel: isSwitchedPlugChannelType(channeltype),
      hasNoBundle: cableless || base.hasNoBundle };
  }
  switch (channeltype) {
    default:
    case ChannelType.Generic:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: false,
        hasNoBundle: true,
      };

    case ChannelType.Unknown:
    case ChannelType.Special:
    case ChannelType.Ignored:
    case ChannelType.ManualSignals:
    case ChannelType.Environment:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: false,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: false,
        hasNoBundle: true,
      };

    case ChannelType.Switch:
      return {
        isSupply: false,
        isLight: true,
        isLight230V: true,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };
    case ChannelType.StaircaseLight:
      return {
        isSupply: false,
        isLight: true,
        isLight230V: true,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };
    case ChannelType.Dimmer:
      return {
        isSupply: false,
        isLight: true,
        isLight230V: true,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.TuneableWhite:
      return {
        isSupply: false,
        isLight: true,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.Rgb:
      return {
        isSupply: false,
        isLight: true,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    // case ChannelType.Rgbw:
    //   return {
    //     isSupply: false,
    //     isLight: true,
    //     canBeConnectedWithPartner: true,
    //     canBeConnectedWithButtonChannel: true,
    //     temperatureAndHeatingChannelTypes: false,
    //     isStatusChannel: false,
    //     isRollo: false,
    //     isHeating: false,
    //     image: image,
    //     isSettableInCustomizing: true,
    //   };

    case ChannelType.Heating:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: true,
        isStatusChannel: false,
        isRollo: false,
        isHeating: true,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.TemperatureSetpoint:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true, //only with heating consumer
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: true,
        isStatusChannel: false,
        isRollo: false,
        isHeating: true,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: false,
        hasNoBundle: false,
      };

    case ChannelType.TemperatureMeasurement:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true, //only with heating consumer
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: true,
        isStatusChannel: false,
        isRollo: false,
        isHeating: true,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: false,
        hasNoBundle: false,
      };

    case ChannelType.Jalousie:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: true,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.Blind:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: true,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.Scene:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: true,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: false,
        hasNoBundle: false,
      };

    case ChannelType.General:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: false,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: false,
        hasNoBundle: true,
      };

    case ChannelType.suply230V:
      return {
        isSupply: true,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.suply400V:
      return {
        isSupply: true,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.suply24V:
      return {
        isSupply: true,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: false,
        hasNoBundle: false,
      };

    case ChannelType.suply12V:
      return {
        isSupply: true,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: false,
        hasNoBundle: false,
      };

    case ChannelType.StatusDisplaySwitch:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: true,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: false,
        canBeConsumer: false,
        hasNoBundle: false,
      };

    case ChannelType.Lock:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: false,
      };

    case ChannelType.DoorLock:
    case ChannelType.Camera:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: true,
      };

    case ChannelType.Lan:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: true,
        isLan: true,
      };

    case ChannelType.Lan2:
      return {
        isSupply: false,
        isLight: false,
        canBeConnectedWithPartner: true,
        canBeConnectedWithButtonChannel: false,
        temperatureAndHeatingChannelTypes: false,
        isStatusChannel: false,
        isRollo: false,
        isHeating: false,
        image: image,
        isSettableInCustomizing: true,
        canBeConsumer: true,
        hasNoBundle: true,
        channelSlotSize: 2,
        isLan: true,
      };
  }
  console.error("unbestimmter channelType");
}

export function isSameLightRolloHeating(
  channelType1: ChannelType,
  channelTye2: ChannelType,
): "isLight" | "isRollo" | "isHeating" | undefined {
  channelType1 = electricalChannelType(channelType1);
  channelTye2 = electricalChannelType(channelTye2);
  if (channelTypeInfo(channelType1).isLight && channelTypeInfo(channelTye2).isLight) {
    return "isLight";
  } else if (channelTypeInfo(channelType1).isRollo && channelTypeInfo(channelTye2).isRollo) {
    return "isRollo";
  } else if (channelTypeInfo(channelType1).isHeating && channelTypeInfo(channelTye2).isHeating) {
    return "isHeating";
  }
  return undefined;
}

export function getChannelTypePicture(channelType: ChannelType | Channel | ButtonChannel) {
  if (typeof channelType == "object") {
    const channel = channelType as Channel;

    if (channel.knxChannel) {
      channelType = channel.knxChannel.channelType;
    } else {
      channelType = channel.channelType;
    }
  }

  if (isPlugChannelType(channelType)) {
    const name = channelType === ChannelType.plug_LAN ? "lan.png"
      : channelType === ChannelType.plug_LAN2 ? "lan2.png"
      : channelType === ChannelType.plug_USB ? "PlugUSB.svg"
      : channelType === ChannelType.plug_TV ? "PlugTV.svg"
      : isSwitchedPlugChannelType(channelType) ? "PlugSwitchable.png" : "Plug.png";
    return state.hostBaseUrl + "images/consumer/" + name;
  }
  if (channelType == ChannelType.Switch) {
    return state.hostBaseUrl + "images/consumer/Bulb_Switch.png";
  } else if (channelType == ChannelType.StaircaseLight) {
    return state.hostBaseUrl + "images/consumer/Bulb_Timer.png";
  } else if (channelType == ChannelType.Dimmer) {
    return state.hostBaseUrl + "images/consumer/Bulb_Dimm.png";
  } else if (channelType == ChannelType.TuneableWhite) {
    return state.hostBaseUrl + "images/consumer/Bulb_TW.png";
  } else if (channelType == ChannelType.Rgb) {
    return state.hostBaseUrl + "images/consumer/rgb.png";
    // } else if (channelType == ChannelType.Rgbw) {
    //   return state.hostBaseUrl + "images/consumer/rgbw.png";
  } else if (channelType == ChannelType.Blind) {
    return state.hostBaseUrl + "images/consumer/shutter.png";
  } else if (channelType == ChannelType.Jalousie) {
    return state.hostBaseUrl + "images/consumer/jalousie.png";
  } else if (
    channelType == ChannelType.Heating ||
    channelType == ChannelType.TemperatureSetpoint ||
    channelType == ChannelType.TemperatureMeasurement
  ) {
    return state.hostBaseUrl + "images/consumer/heating.png";
  } else if (channelType == ChannelType.OperationMode) {
    return state.hostBaseUrl + "images/consumer/operationmode.png";
  } else if (channelType == ChannelType.Scene) {
    return state.hostBaseUrl + "images/various/scene.png";
  } else if (channelType == ChannelType.Generic) {
    return "";
  } else if (channelType == ChannelType.suply400V) {
    return state.hostBaseUrl + "images/consumer/400V.png";
  } else if (channelType == ChannelType.suply230V) {
    return state.hostBaseUrl + "images/consumer/200V.png";
  } else if (channelType == ChannelType.PlugSwitchable) {
    return state.hostBaseUrl + "images/consumer/PlugSwitchable.png";
  } else if (channelType == ChannelType.Plug) {
    return state.hostBaseUrl + "images/consumer/Plug.png";
  } else if (channelType == ChannelType.Lan) {
    return state.hostBaseUrl + "images/consumer/lan.png";
  } else if (channelType == ChannelType.Lan2) {
    return state.hostBaseUrl + "images/consumer/lan2.png";
  } else if (channelType == ChannelType.IntermediateDistribution) {
    return state.hostBaseUrl + "images/consumer/IntermediateDistribution.svg";
  } else if (channelType == ChannelType.Lock) {
    return state.hostBaseUrl + "images/consumer/InputSwitch.png";
  }
  return "";
}

export function channelTypeFromString(channelType: string): ChannelType {
  let res = ChannelType.Unknown;

  channelType = channelType.toLowerCase();
  for (const i in ChannelType) {
    if (i.toString().toLowerCase() === channelType) {
      res = ChannelType[i as keyof typeof ChannelType];
    }
  }

  return res;
}

export function areChannelTypesCompatible(
  channelType1: ChannelType,
  channelType2: ChannelType,
  targetDevice: Device,
): boolean {
  channelType1 = electricalChannelType(channelType1);
  channelType2 = electricalChannelType(channelType2);
  if (channelType1 == channelType2) {
    return true;
  }

  const info1 = channelTypeInfo(channelType1);
  const info2 = channelTypeInfo(channelType2);

  if (info1.isLight230V && info2.isLight230V) {
    //
    // Dimmer auf Switch ist erlaubt
    //
    return true;
  }

  if (info1.isRollo && info2.isRollo) {
    return true;
  }

  if (info1.temperatureAndHeatingChannelTypes && info2.temperatureAndHeatingChannelTypes) {
    return true;
  }

  if (targetDevice.isButton && targetDevice.template?.knxProductData.hasScript_setChannelType) {
    if (info1.isLight && info2.isLight) {
      return true;
    }
  }

  if (channelType1 == ChannelType.Generic) {
    if (
      channelType2 != ChannelType.Unknown &&
      channelType2 != ChannelType.Environment &&
      channelType2 != ChannelType.General &&
      channelType2 != ChannelType.Generic
    ) {
      return true;
    }
  }

  if (channelType2 == ChannelType.Generic) {
    return areChannelTypesCompatible(channelType2, channelType1, targetDevice);
  }
  return false;
}
